import { InputSettings, TemplateField, TemplateFormula } from '../../../types/template-types';
import { getField } from '../../selectors';
import { CriteriaType, GENERIC_FIELD_NAME, InputSelectionType } from './constants';

const formatInputValue = (input: InputSettings): string => {
  // If it is a field selection, format the input value with brackets so it uses the field value
  return input.inputValueSelectionType === InputSelectionType.PickField ? `[${input.inputValue}]` : `${input.inputValue}`;
};

export const mapCriteriaTypeToEnum = (criteriaType) => {
  switch (criteriaType) {
    case 1:
      return CriteriaType.Email;
    case 2:
      return CriteriaType.Exactly;
    case 3:
      return CriteriaType.Contains;
    case 4:
      return CriteriaType.NotContains;
    case 5:
      return CriteriaType.Length;
    case 6:
      return CriteriaType.GreaterThan;
    case 7:
      return CriteriaType.GreaterThanOrEqualTo;
    case 8:
      return CriteriaType.LessThan;
    case 9:
      return CriteriaType.LessThanOrEqualTo;
    case 10:
      return CriteriaType.EqualTo;
    case 11:
      return CriteriaType.NotEqualTo;
    case 12:
      return CriteriaType.Between;
    case 13:
      return CriteriaType.NotBetween;
    default:
      return CriteriaType.None;
  }
};

export const getFieldOptions = (fields: TemplateField[], fieldType: number, isMultiSelected?: boolean, fieldName?: string) => {
  let filteredFields = fields.filter((field) => field.type === fieldType);

  if (!isMultiSelected && fieldName) {
    filteredFields = filteredFields.filter((field) => field.name !== fieldName);
  }

  return filteredFields.map((field) => ({ value: field.name, text: field.name }));
};

export const checkFormulaMatch = (state, fieldIds) => {
  if (!fieldIds || fieldIds.length <= 1) {
    return false;
  }

  // Get the first field's validation settings to compare against
  const firstField = getField(state, fieldIds[0]);
  const firstValidation = firstField?.validationSettings?.[0];

  if (!firstValidation?.criteriaType || !firstValidation.inputValues) {
    return false;
  }

  const criteriaType = firstValidation.criteriaType;
  const firstFormula = mapCriteriaToFormula(criteriaType, GENERIC_FIELD_NAME, firstValidation.inputValues);

  if (!firstFormula.value) {
    return false;
  }

  // Check all remaining fields against the first formula to ensure equivalence
  for (let i = 1; i < fieldIds.length; i++) {
    const field = getField(state, fieldIds[i]);
    const currentValidation = field?.validationSettings?.[0];

    // Check validation settings
    if (!currentValidation?.criteriaType || !currentValidation.inputValues) {
      return false;
    }

    // Compare criteria types
    if (currentValidation.criteriaType !== criteriaType) {
      return false;
    }

    // Generate and compare formulas
    const currentFormula = mapCriteriaToFormula(criteriaType, GENERIC_FIELD_NAME, currentValidation.inputValues);
    if (!currentFormula?.value || currentFormula.value !== firstFormula.value) {
      return false;
    }
  }

  return true;
};

const generateComparisonFormula = (field: string, operator: string, input: InputSettings): string => {
  const value = formatInputValue(input);
  return `=If(${field} ${operator} ${value}, True(), False())`;
};

// Check if the criteria requires input values
const needsInputValues = (criteria: CriteriaType): boolean => {
  return criteria !== CriteriaType.Email;
};

// Map validation criteria to a formula expression
export const mapCriteriaToFormula = (criteria: CriteriaType, fieldName: string, inputValues: InputSettings[]): TemplateFormula => {
  const formula: TemplateFormula = {
    key: 'FieldValidation',
    value: ''
  };

  // Format field name with brackets
  const formattedFieldName = `[${fieldName}]`;

  if (!inputValues || (inputValues.length === 0 && needsInputValues(criteria))) {
    return formula;
  }

  switch (criteria) {
    case CriteriaType.Email:
      formula.value = `=RegexTest(${formattedFieldName}, "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$")`;
      break;
    case CriteriaType.Exactly:
      formula.value = `=Exact(${formattedFieldName}, "${inputValues[0].inputValue}")`;
      break;
    case CriteriaType.Contains:
      formula.value = `=If(Find("${inputValues[0].inputValue}", ${formattedFieldName}) > -1, True(), False())`;
      break;
    case CriteriaType.NotContains:
      formula.value = `=If(Find("${inputValues[0].inputValue}", ${formattedFieldName}) = -1, True(), False())`;
      break;
    case CriteriaType.Length:
      formula.value = `=If(Len(${formattedFieldName}) = ${inputValues[0].inputValue}, True(), False())`;
      break;
    case CriteriaType.GreaterThan:
      formula.value = generateComparisonFormula(formattedFieldName, '>', inputValues[0]);
      break;
    case CriteriaType.GreaterThanOrEqualTo:
      formula.value = generateComparisonFormula(formattedFieldName, '>=', inputValues[0]);
      break;
    case CriteriaType.LessThan:
      formula.value = generateComparisonFormula(formattedFieldName, '<', inputValues[0]);
      break;
    case CriteriaType.LessThanOrEqualTo:
      formula.value = generateComparisonFormula(formattedFieldName, '<=', inputValues[0]);
      break;
    case CriteriaType.EqualTo:
      formula.value = generateComparisonFormula(formattedFieldName, '=', inputValues[0]);
      break;
    case CriteriaType.NotEqualTo:
      formula.value = generateComparisonFormula(formattedFieldName, '<>', inputValues[0]);
      break;
    case CriteriaType.Between: {
      if (inputValues.length == 2) {
        const value1 = formatInputValue(inputValues[0]);
        const value2 = formatInputValue(inputValues[1]);
        formula.value = `=If(And(${formattedFieldName} > ${value1}, ${formattedFieldName} < ${value2}), True(), False())`;
      }
      break;
    }
    case CriteriaType.NotBetween: {
      if (inputValues.length == 2) {
        const value1 = formatInputValue(inputValues[0]);
        const value2 = formatInputValue(inputValues[1]);
        formula.value = `=If(Or(${formattedFieldName} < ${value1}, ${formattedFieldName} > ${value2}), True(), False())`;
      }
      break;
    }
    default:
      break;
  }

  return formula;
};
